import { computed, reactive } from 'vue';
import {
  currentLocale as i18nCurrentLocale,
  enabledLocales as i18nEnabledLocales,
} from '@/app/i18n';
import { faceDetection } from '@/services/FaceDetection';
import { kioskDesign } from '@/services/KioskDesign';
import { kioskHardware } from '@/services/KioskHardware';
import { kioskStyle } from '@/services/KioskStyle';
import { payment } from '@/services/Payment';
import { portraitGeneration } from '@/services/PortraitGeneration';
import { printing } from '@/services/Printing';
import { session } from '@/services/Session';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';

export const store = reactive({
  // Kiosk design & parameters
  kiosk: computed(() => kioskDesign.kiosk),
  kioskPartner: computed(() => kioskDesign.partner),
  kioskFontFaceCssCode: computed(() => kioskDesign.fontFaceCssCode),

  // Locale
  currentLocale: computed(() => i18nCurrentLocale.value),
  enabledLocales: computed(() => i18nEnabledLocales.value),

  // Session
  sessionId: computed(() => session.id),
  sessionCreatedAt: computed(() => session.createdAt),
  gophotoCode: computed(() => session.gophotoCode),

  // Flag set to true while Session._doEnd() is executing.
  // Allows reactive watchers in components to skip processing during the
  // reset window when store properties are being cleared in stages.
  // Without this guard, watchers see partially-cleared state (e.g., photos
  // empty but footerProgressIndicatorCurrentStep still at the old value)
  // and may trigger side effects for a session that no longer exists.
  isResettingSession: false,

  // Styles
  styles: computed(() => kioskStyle.styles),
  // Normalize to null (not undefined) when no style is selected.
  // Array.find() returns undefined on miss, but downstream code checks
  // `store.selectedStyle === null` or uses truthiness -- the ?? null
  // coercion ensures both patterns work correctly.
  selectedStyle: computed(
    () => kioskStyle.getStyleById(kioskStyle.selectedStyleId) ?? null
  ),

  // Kiosk hardware
  kioskId: computed(() => kioskHardware.kioskId),
  kioskMode: computed(() => kioskHardware.kioskMode),
  isMiniPCKiosk: computed(() => kioskHardware.isMiniPCKiosk),
  device: computed(() => kioskHardware.device),
  isMobileDevice: computed(() => kioskHardware.isMobileDevice),
  isDevMode: computed(() => kioskHardware.isDevMode),
  viewportWidth: computed(() => kioskHardware.viewportWidth),
  viewportHeight: computed(() => kioskHardware.viewportHeight),
  viewportIsPortrait: computed(() => kioskHardware.viewportIsPortrait),
  isVerticalKiosk: computed(() => kioskHardware.isVerticalKiosk),

  // UI overlays / shell flags moved to a Pinia store (`useUiStore`,
  // `@/app/stores/ui`). Includes the overlay queue, soft/hard restart
  // actions, and `showLogoStartOverButton` / `showPrivacyPolicyPopup` /
  // `footerProgressIndicatorCurrentStep` / `showUploadedPhotos` flags.

  // Developer flags moved to a Pinia store (`useDeveloperStore`,
  // `@/app/stores/developer`). They persist across sessions via
  // localStorage with the legacy `kiosk_developer_<field>` key layout.

  // Webcam photos & faces
  photos: computed(() => faceDetection.photos),
  photosUploaded: computed(() => faceDetection.photosUploaded),
  photosHasSkippedMobileUploads: computed(
    () => faceDetection.hasSkippedMobileUploads
  ),
  faces: computed(() => faceDetection.faces),
  facesSelected: computed(() => faceDetection.facesSelected),
  photoUploadNextStep: computed(() => faceDetection.nextStep),

  // Portraits
  portraits: computed(() => portraitGeneration.portraits),
  groupPortraits: computed(() => portraitGeneration.groupPortraits),
  portraitsIsGenerating: computed(() => portraitGeneration.isGenerating),
  portraitGenerationAttemptsLeft: computed(
    () => portraitGeneration.attemptsLeft
  ),

  // Printing
  printingQueue: computed(() => printing.queue),
  printingTotalCount: computed(() => printing.totalCount),
  printingHasErrors: computed(() => printing.hasErrors),
  printingInProgress: computed(() => printing.isPrintingInProgress),

  // Payment
  paymentStepLink: computed(() => payment.paymentStepLink),
});

exposeToGlobalScope(store, 'store');
